import { useEffect, useState } from 'react';
import type { FloorPlannerLayout } from '@repo/frontend-types';

import Config from '../../common/Config';
import { fetchLiveTwinLayout } from './liveTwinLayout';

/**
 * Can this twin's live model be used as the starting plan, and if not, why not?
 *
 * The Floor Plan chooser's *Start from this twin* card must be offered **honestly**:
 * rendered disabled with the reason visible, never as a button that throws on click (spec FR2,
 * epic ledger row 27a). Answering that question needs the live tree — which needs a fetch — so the
 * work happens here, before the click, and the click itself is only "load and save what we already
 * have".
 *
 * Keeping it in a hook rather than in the click handler is what makes FR2 testable without a DOM,
 * and it is why `loading` is a real state: a 553-entity twin (the #1127 validation set's largest)
 * takes seconds to fetch and convert, and an option that sat silently disabled through that would
 * read as "this twin has no model".
 */
export type LiveTwinPlanState =
  /** Resolving the org, fetching the tree, or converting it. The option shows a loading state. */
  | { status: 'loading' }
  /** Ready. `layout` is the plan the click handler loads — already converted, so the click cannot fail. */
  | {
      status: 'available';
      layout: FloorPlannerLayout;
      /**
       * What the conversion could not import, if anything — `plan-entity-identity`.
       *
       * Adoption succeeded; some entities did not come across because they have no identity in the
       * live model. The operator has to know before activating, because an absent entity reads as a
       * REMOVAL in Go Live's delta and pressing through that destroys the live one.
       */
      warning?: string;
      /** How many entities the conversion skipped — the chooser's count and the saved note (`floor-plan-page-start`). */
      skipped: number;
    }
  /** Cannot be offered. `reason` is shown to the user, so it must be a sentence, not an error code. */
  | { status: 'unavailable'; reason: string };

export interface UseLiveTwinPlanParams {
  /**
   * The twin being set up, from the wizard's `?twinBid=`.
   *
   * NOT the floorplan store's `twinBID`, which the onboarding wizard keys on the *session* id
   * (`OnboardingPlanner`'s `sessionId`) rather than the twin. The two are different values with the
   * same name, and mixing them up is the easiest mistake to make in this file.
   */
  twinBid: string | undefined;
  accessToken: string | undefined;
  /** The org the OPERATOR has selected (`authMetadata.orgBID`) — an input to the check, never the answer. */
  selectedOrgBid: string | undefined;
  /**
   * When false the fetch is not attempted at all and the state stays `unavailable`.
   *
   * The caller passes false where seeding would destroy authored work (FR2.2). Skipping rather than
   * disabling is deliberate: fetching a full ten-deep entity tree to populate a button the user
   * must not press is pure cost.
   */
  enabled: boolean;
}

/** Copy for the states a user can actually reach. Kept together so the wording is reviewable. */
const REASON = {
  noModel: 'This twin has no live model yet',
  hasPlan: 'This twin already has a plan in progress',
  noContext: 'Missing twin context — reload the page',
  noOrgSelected:
    'No organisation is selected, so this twin’s model cannot be read. Open the twin first, then return here.',
} as const;

export const useLiveTwinPlan = ({
  twinBid,
  accessToken,
  selectedOrgBid,
  enabled,
}: UseLiveTwinPlanParams): LiveTwinPlanState => {
  const [state, setState] = useState<LiveTwinPlanState>({ status: 'loading' });

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'unavailable', reason: REASON.hasPlan });
      return;
    }
    if (!twinBid) {
      setState({ status: 'unavailable', reason: REASON.noContext });
      return;
    }
    // The access token is resolved by an effect in the host component, so its absence is a genuine
    // loading state that settles on its own within a render or two.
    if (!accessToken) {
      setState({ status: 'loading' });
      return;
    }
    // A MISSING SELECTED ORG IS TERMINAL, NOT LOADING — and this cost a real validation run to
    // learn. `authMetadata.orgBID` is not restored from the Auth0 claims; `AuthMetadataRestorer`
    // deliberately leaves it alone and it is only populated once the user has picked an org (the
    // twin page writes it to localStorage, and the store validates it against the org list).
    // Entering `/onboarding` in a fresh session therefore has no org, nothing in the app will
    // supply one without a user action, and reporting `loading` left the option disabled with a
    // spinner FOREVER — the precise failure FR2 exists to prevent, arriving by the back door.
    // The wizard's Go Live has the same dependency and refuses out loud ("Missing twin context");
    // this refuses out loud too, and re-runs the moment an org arrives.
    if (!selectedOrgBid) {
      setState({ status: 'unavailable', reason: REASON.noOrgSelected });
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setState({ status: 'loading' });

    (async () => {
      // Org resolution, the bounded dGraph read and the conversion all live in `fetchLiveTwinLayout`
      // — shared with the Observers stage's running-model read (`camera-observer-setup-attention`),
      // so the two cannot disagree about whose namespace a twin is in or what a failed read means.
      const outcome = await fetchLiveTwinLayout({
        middlewareApi: Config.middlewareApi,
        accessToken,
        twinBid,
        selectedOrgBid,
        signal: controller.signal,
        logTag: 'adopt-plan',
      });
      if (cancelled || outcome.status === 'cancelled') return;
      if (outcome.status === 'failed') {
        setState({ status: 'unavailable', reason: outcome.reason });
        return;
      }
      if (outcome.status === 'none') {
        setState({ status: 'unavailable', reason: REASON.noModel });
        return;
      }
      setState({
        status: 'available',
        layout: outcome.layout,
        warning: outcome.warning,
        skipped: outcome.skipped,
      });
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [twinBid, accessToken, selectedOrgBid, enabled]);

  return state;
};
